import { useEffect, useRef, useState } from 'react';
import type { BatchAiCorrectionProgress } from './useBatchAiCorrection';
import { AI_BATCH_CHUNK_SIZE } from './useBatchAiCorrection';

/**
 * Fração do próximo marco (pedaço em voo) até onde a barra pode avançar sozinha —
 * nunca alcança o próprio marco, para não parecer concluída antes da resposta real.
 */
const SOFT_CAP_RATIO = 0.92;
/** Intervalo entre passos da animação. */
const STEP_MS = 400;
/** Fração da distância restante até o alvo coberta a cada passo (suaviza a chegada). */
const EASE_FACTOR = 0.12;

/** Indica se o usuário pediu para reduzir animações no sistema operacional. */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Anima visualmente o progresso de {@link useBatchAiCorrection} entre atualizações
 * reais (uma por pedaço concluído). Sem isso, um lote que caiba em um único pedaço
 * (ex.: 3 de 20) pula direto de 0% a 100%, sem nenhuma sensação de progresso.
 *
 * Ao chegar uma atualização real, o valor exibido salta para ela imediatamente e
 * passa a avançar sozinho, em passos suavizados, até {@link SOFT_CAP_RATIO} do
 * próximo marco — sem nunca alcançá-lo, já que não há sinal do servidor até o
 * pedaço em voo resolver. A próxima atualização real substitui o alvo e repete o
 * ciclo.
 *
 * @param progress - Progresso real retornado por `useBatchAiCorrection` (`null` fora de uma correção em andamento)
 * @returns Percentual (0–100) a exibir na `ProgressBar`
 *
 * @example
 * ```tsx
 * const { progress } = useBatchAiCorrection();
 * const displayPercentage = useAnimatedBatchProgress(progress);
 * ```
 */
export function useAnimatedBatchProgress(progress: BatchAiCorrectionProgress | null): number {
  const [displayPercentage, setDisplayPercentage] = useState(0);
  const targetRef = useRef(0);

  useEffect(() => {
    if (!progress || progress.total <= 0) {
      setDisplayPercentage(0);
      return;
    }

    const { completed, total } = progress;
    const realPercentage = (completed / total) * 100;
    setDisplayPercentage(realPercentage);

    if (completed >= total || prefersReducedMotion()) return;

    const nextBoundary = Math.min(completed + AI_BATCH_CHUNK_SIZE, total);
    const nextPercentage = (nextBoundary / total) * 100;
    targetRef.current = realPercentage + (nextPercentage - realPercentage) * SOFT_CAP_RATIO;

    const interval = setInterval(() => {
      setDisplayPercentage(current => current + (targetRef.current - current) * EASE_FACTOR);
    }, STEP_MS);

    return () => clearInterval(interval);
  }, [progress]);

  return displayPercentage;
}
